import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, RotateCcw, Share2, XCircle } from 'lucide-react';
import {
  buildQuizAttempt,
  isValidStoredResult,
  type MicroQuizData,
  type MicroQuizQuestion,
  type MicroQuizResult,
} from '../../lib/microQuiz';

type QuizStep = 'intro' | 'questions' | 'result';

interface MicroQuizProps {
  slug: string;
  quiz: MicroQuizData;
  max_questions?: number;
  max_answers?: number;
  className?: string;
}

const DEFAULT_TITLE = 'Teste seu conhecimento sobre o assunto';

const MicroQuiz: React.FC<MicroQuizProps> = ({
  slug,
  quiz,
  max_questions = 3,
  max_answers = 3,
  className = '',
}) => {
  const [step, setStep] = useState<QuizStep>('intro');
  const [questions, setQuestions] = useState<MicroQuizQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [result, setResult] = useState<MicroQuizResult | null>(null);
  const [shareFeedback, setShareFeedback] = useState<string>('');

  const resolvedMaxQuestions = quiz.max_questions ?? max_questions;
  const resolvedMaxAnswers = quiz.max_answers ?? max_answers;
  const quizTitle = quiz.title || DEFAULT_TITLE;
  const storageKey = useMemo(() => `gas2:microquiz:${slug}`, [slug]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const stored = window.localStorage.getItem(storageKey);
      if (!stored) return;

      const parsed: unknown = JSON.parse(stored);
      if (!isValidStoredResult(parsed)) return;

      setResult(parsed);
      setStep('result');
    } catch {
      // Ignore localStorage parse errors and keep default state.
    }
  }, [storageKey]);

  const currentQuestion = questions[currentIndex];
  const selectedOption = currentQuestion?.options.find((option) => option.id === selectedOptionId);
  const isLastQuestion = currentIndex === questions.length - 1;

  const startQuiz = () => {
    const nextQuestions = buildQuizAttempt(quiz, resolvedMaxQuestions, resolvedMaxAnswers);

    setQuestions(nextQuestions);
    setCurrentIndex(0);
    setSelectedOptionId(null);
    setIsChecked(false);
    setAnswers({});
    setShareFeedback('');

    if (nextQuestions.length > 0) {
      setStep('questions');
    }
  };

  const checkAnswer = () => {
    if (!currentQuestion || !selectedOption || isChecked) return;

    setAnswers((previous) => ({
      ...previous,
      [currentQuestion.id]: selectedOption.correct,
    }));
    setIsChecked(true);
  };

  const finishQuiz = () => {
    const correctAnswers = Object.values(answers).filter(Boolean).length;
    const nextResult: MicroQuizResult = {
      correctAnswers,
      totalQuestions: questions.length,
      finishedAt: new Date().toISOString(),
    };

    setResult(nextResult);
    setStep('result');
    setShareFeedback('');

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, JSON.stringify(nextResult));
    }
  };

  const goToNextQuestion = () => {
    if (!isChecked) return;

    if (isLastQuestion) {
      finishQuiz();
      return;
    }

    setCurrentIndex((index) => index + 1);
    setSelectedOptionId(null);
    setIsChecked(false);
    setShareFeedback('');
  };

  const retryQuiz = () => {
    startQuiz();
  };

  const handleShare = async () => {
    if (!result || typeof window === 'undefined') return;

    const baseUrl = window.location.href.split('#')[0];
    const quizUrl = `${baseUrl}#quiz`;
    const message = `Fiz ${result.correctAnswers}/${result.totalQuestions} no quiz deste artigo no Gas2.`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: quizTitle,
          text: message,
          url: quizUrl,
        });
        setShareFeedback('Resultado compartilhado.');
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(quizUrl);
        setShareFeedback('Link copiado para a area de transferencia.');
        return;
      }

      setShareFeedback('Nao foi possivel compartilhar neste navegador.');
    } catch {
      setShareFeedback('Compartilhamento cancelado.');
    }
  };

  const explanationText = useMemo(() => {
    if (!isChecked || !currentQuestion || !selectedOption) return '';

    if (!selectedOption.correct && selectedOption.explanation) {
      return selectedOption.explanation;
    }

    return currentQuestion.explanation || selectedOption.explanation || '';
  }, [currentQuestion, isChecked, selectedOption]);

  const scorePercent = result && result.totalQuestions > 0
    ? Math.round((result.correctAnswers / result.totalQuestions) * 100)
    : 0;

  const resultTone =
    scorePercent === 100
      ? {
          icon: '🎉',
          label: 'Excelente! Voce acertou tudo.',
          classes: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200',
        }
      : scorePercent >= 50
      ? {
          icon: '🙂',
          label: 'Bom resultado! Mais uma tentativa pode fechar 100%.',
          classes: 'border-amber-400/40 bg-amber-500/10 text-amber-200',
        }
      : {
          icon: '🛠️',
          label: 'Voce esta no caminho. Refaca para fixar melhor.',
          classes: 'border-rose-400/40 bg-rose-500/10 text-rose-200',
        };

  return (
    <section id="quiz" className={`rounded-2xl border border-slate-800 bg-slate-900/70 p-5 sm:p-6 ${className}`}>
      {(step === 'intro' || step === 'result') && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg sm:text-xl font-semibold text-white">{quizTitle}</h2>

          {step === 'intro' && (
            <button
              type="button"
              onClick={startQuiz}
              className="inline-flex items-center justify-center rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-300"
            >
              Iniciar Teste
            </button>
          )}
        </div>
      )}

      {step === 'questions' && currentQuestion && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-400">
            <p>
              Pergunta {currentIndex + 1} de {questions.length}
            </p>
            <p>{Math.round(((currentIndex + 1) / questions.length) * 100)}%</p>
          </div>

          <h3 className="text-lg sm:text-xl font-semibold text-white leading-snug">{currentQuestion.question}</h3>

          <div className="space-y-2.5">
            {currentQuestion.options.map((option) => {
              const isSelected = selectedOptionId === option.id;

              let optionClasses = 'border-slate-700 bg-slate-900 text-slate-200 hover:border-amber-400/50';
              if (isChecked && option.correct) {
                optionClasses = 'border-emerald-400/70 bg-emerald-500/15 text-emerald-100';
              } else if (isChecked && isSelected && !option.correct) {
                optionClasses = 'border-rose-400/70 bg-rose-500/15 text-rose-100';
              } else if (isSelected) {
                optionClasses = 'border-amber-400/70 bg-amber-500/10 text-amber-200';
              }

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => !isChecked && setSelectedOptionId(option.id)}
                  disabled={isChecked}
                  className={`w-full rounded-xl border px-4 py-3 text-left text-sm sm:text-base transition-colors ${optionClasses}`}
                >
                  {option.text}
                </button>
              );
            })}
          </div>

          {!isChecked ? (
            <button
              type="button"
              onClick={checkAnswer}
              disabled={!selectedOptionId}
              className="inline-flex items-center justify-center rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Checar Resposta
            </button>
          ) : (
            <div className="space-y-4">
              <div
                className={`rounded-xl border p-4 ${
                  selectedOption?.correct
                    ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100'
                    : 'border-rose-400/40 bg-rose-500/10 text-rose-100'
                }`}
              >
                <p className="flex items-center gap-2 text-sm font-semibold">
                  {selectedOption?.correct ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                      Resposta correta!
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" strokeWidth={2} />
                      Nao foi dessa vez.
                    </>
                  )}
                </p>

                {explanationText && <p className="mt-2 text-sm leading-relaxed">{explanationText}</p>}
              </div>

              <button
                type="button"
                onClick={goToNextQuestion}
                className="inline-flex items-center justify-center rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-white"
              >
                {isLastQuestion ? 'Ver Resultado' : 'Proxima Pergunta'}
              </button>
            </div>
          )}
        </div>
      )}

      {step === 'result' && result && (
        <div className="mt-5 space-y-4">
          <div className={`rounded-xl border p-4 ${resultTone.classes}`}>
            <p className="text-2xl" aria-hidden="true">
              {resultTone.icon}
            </p>
            <p className="mt-2 text-base font-semibold">{resultTone.label}</p>
            <p className="mt-1 text-sm">
              Sua nota: {result.correctAnswers}/{result.totalQuestions} ({scorePercent}%)
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={retryQuiz}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-300"
            >
              <RotateCcw className="h-4 w-4" strokeWidth={2} />
              Refazer Teste
            </button>

            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-600 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:border-slate-500"
            >
              <Share2 className="h-4 w-4" strokeWidth={2} />
              Compartilhar
            </button>
          </div>

          {shareFeedback && <p className="text-xs text-slate-400">{shareFeedback}</p>}
        </div>
      )}
    </section>
  );
};

export default MicroQuiz;
